import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTodaysPuzzle, supabase, getCurrentUser } from './supabaseClient';
import MinimalFooter from './MinimalFooter';

function SudokuPuzzle({ navigateTo, goBack, difficulty, mode, attemptState, markAttemptUsed, markPuzzleCompleted }) {
  
  // FALLBACK PUZZLE (Emergency use only)
  const FALLBACK_PUZZLE = {
    puzzle_grid: [
      [5,3,0,0,7,0,0,0,0],
      [6,0,0,1,9,5,0,0,0],
      [0,9,8,0,0,0,0,6,0],
      [8,0,0,0,6,0,0,0,3],
      [4,0,0,8,0,3,0,0,1],
      [7,0,0,0,2,0,0,0,6],
      [0,6,0,0,0,0,2,8,0],
      [0,0,0,4,1,9,0,0,5],
      [0,0,0,0,8,0,0,7,9]
    ],
    solution_grid: [
      [5,3,4,6,7,8,9,1,2],
      [6,7,2,1,9,5,3,4,8],
      [1,9,8,3,4,2,5,6,7],
      [8,5,9,7,6,1,4,2,3],
      [4,2,6,8,5,3,7,9,1],
      [7,1,3,9,2,4,8,5,6],
      [9,6,1,5,3,7,2,8,4],
      [2,8,7,4,1,9,6,3,5],
      [3,4,5,2,8,6,1,7,9]
    ]
  };

  // Puzzle data state (NEW - replaces hardcoded puzzle)
  const [initialPuzzle, setInitialPuzzle] = useState(null);
  const [solution, setSolution] = useState(null);
  const [isLoadingPuzzle, setIsLoadingPuzzle] = useState(true);
  const [puzzleError, setPuzzleError] = useState(null);
  
  // Puzzle state - 0 means empty cell
  const [grid, setGrid] = useState([]);
  const [notes, setNotes] = useState(Array(9).fill(null).map(() => 
    Array(9).fill(null).map(() => [])
  ));
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isNotesMode, setIsNotesMode] = useState(false);
  const notesModeRef = useRef(false); // NOTES FIX: Track current notes mode with ref for immediate access
  const [mistakes, setMistakes] = useState(0);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(true);
  const [startTime] = useState(Date.now()); // Track when puzzle started
  const [history, setHistory] = useState([]);
  const [highlightNumber, setHighlightNumber] = useState(null);
  const [puzzleComplete, setPuzzleComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ANTI-CHEAT: Check if puzzle already attempted/completed on mount
  useEffect(() => {
    checkPuzzleState();
  }, []);

  const checkPuzzleState = async () => {
    // ANTI-CHEAT: Check both database (logged-in) and localStorage (guest)
    const today = new Date().toISOString().split('T')[0];
    const stateKey = `puzzle_state_${difficulty}_${today}`;
    
    try {
      // Check if logged in
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Logged-in user: check database
        const { data, error } = await supabase
          .from('solves')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('difficulty', difficulty)
          .eq('puzzle_date', today)
          .maybeSingle();
        
        if (data) {
          // Puzzle already attempted or completed
          console.log('ANTI-CHEAT: Puzzle already attempted, redirecting');
          alert('You have already attempted this puzzle today.');
          navigateTo('difficulty');
          return;
        }
      } else {
        // Guest: check localStorage
        const guestState = localStorage.getItem(stateKey);
        if (guestState) {
          const state = JSON.parse(guestState);
          if (state.attempted || state.completed) {
            console.log('ANTI-CHEAT: Guest already attempted this puzzle, redirecting');
            alert('You have already attempted this puzzle today.');
            navigateTo('difficulty');
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error checking puzzle state:', error);
      // On error, allow play but log it
    }
  };

  // NEW: Load puzzle from database
  useEffect(() => {
    loadPuzzle();
  }, [difficulty]);

  // NOTES FIX: Keep ref in sync with notes mode state for immediate, reliable access
  useEffect(() => {
    notesModeRef.current = isNotesMode;
  }, [isNotesMode]);

  const loadPuzzle = async () => {
    setIsLoadingPuzzle(true);
    setPuzzleError(null);
    
    try {
      const result = await getTodaysPuzzle(difficulty);
      
      if (result && result.success === true) {
        console.log('Puzzle loaded successfully:', result.puzzle_id);
        setInitialPuzzle(result.puzzle_grid);
        setSolution(result.solution_grid);
        setGrid(result.puzzle_grid.map(row => [...row]));
        setIsLoadingPuzzle(false);
        return;
      }
      
      if (result && result.success === false) {
        console.warn('Using fallback puzzle. Reason:', result.error);
        setPuzzleError(result);
        
        setInitialPuzzle(FALLBACK_PUZZLE.puzzle_grid);
        setSolution(FALLBACK_PUZZLE.solution_grid);
        setGrid(FALLBACK_PUZZLE.puzzle_grid.map(row => [...row]));
        setIsLoadingPuzzle(false);
        return;
      }
      
      console.error('Unexpected puzzle state, using fallback');
      setPuzzleError({ error: 'unexpected' });
      
      setInitialPuzzle(FALLBACK_PUZZLE.puzzle_grid);
      setSolution(FALLBACK_PUZZLE.solution_grid);
      setGrid(FALLBACK_PUZZLE.puzzle_grid.map(row => [...row]));
      setIsLoadingPuzzle(false);
      
    } catch (error) {
      console.error('Error loading puzzle:', error);
      setPuzzleError({ error: 'exception' });
      
      setInitialPuzzle(FALLBACK_PUZZLE.puzzle_grid);
      setSolution(FALLBACK_PUZZLE.solution_grid);
      setGrid(FALLBACK_PUZZLE.puzzle_grid.map(row => [...row]));
      setIsLoadingPuzzle(false);
    }
  };

  // ANTI-CHEAT: Mark attempt as used immediately on mount
  useEffect(() => {
    if (difficulty && mode && markAttemptUsed) {
      markAttemptUsed(difficulty, mode);
    }
  }, []); // Only run once on mount

  // Detect mobile/desktop
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Timer - only runs in Standard mode
  useEffect(() => {
    let interval;
    if (isRunning && mode === 'standard' && !puzzleComplete) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, mode, puzzleComplete]);

  // Count how many of a specific number have been placed
  const countNumberInstances = useCallback((num) => {
    let count = 0;
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (grid[r][c] === num) count++;
      }
    }
    return count;
  }, [grid]);

  // Keyboard support
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedCell) return;
      
      const key = e.key;
      
      if (key >= '1' && key <= '9') {
        e.preventDefault();
        const num = parseInt(key);
        
        // KEYBOARD FIX: Check if number is completed (all 9 instances placed)
        const instanceCount = countNumberInstances(num);
        const isCompleted = instanceCount === 9;
        
        // Only allow input if number is not completed
        if (!isCompleted) {
          handleNumberInput(num);
        }
        // If completed, do nothing (same behavior as disabled dial pad button)
      }
      
      if (key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
        handleClear();
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        e.preventDefault();
        const { row, col } = selectedCell;
        let newRow = row;
        let newCol = col;

        if (key === 'ArrowUp') newRow = Math.max(0, row - 1);
        if (key === 'ArrowDown') newRow = Math.min(8, row + 1);
        if (key === 'ArrowLeft') newCol = Math.max(0, col - 1);
        if (key === 'ArrowRight') newCol = Math.min(8, col + 1);

        setSelectedCell({ row: newRow, col: newCol });
        if (grid[newRow][newCol] !== 0) {
          setHighlightNumber(grid[newRow][newCol]);
        } else {
          setHighlightNumber(null);
        }
      }

      if (key === 'n' || key === 'N') {
        e.preventDefault();
        setIsNotesMode(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedCell, grid]); // Functions are stable, no need to list them

  // ANTI-CHEAT: Persist completion for guests (logged-in users save via solves table)
  const persistPuzzleCompletion = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const stateKey = `puzzle_state_${difficulty}_${today}`;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Guest only: save to localStorage
        localStorage.setItem(stateKey, JSON.stringify({
          attempted: true,
          completed: true,
          mode: mode,
          date: today
        }));
      }
      // Logged-in users: completion saved via solves table in ResultScreen
    } catch (error) {
      console.error('Error persisting completion:', error);
    }
  }, [difficulty, mode]);

  // Check if puzzle is complete
  useEffect(() => {
    if (!solution) return; // Don't check if solution not loaded yet
    
    const checkComplete = () => {
      for (let r = 0; r < 9; r++) {
        for (let c = 0; c < 9; c++) {
          if (grid[r][c] === 0 || grid[r][c] !== solution[r][c]) {
            return false;
          }
        }
      }
      return true;
    };

    if (checkComplete()) {
      setPuzzleComplete(true);
      setIsRunning(false);
      // ANTI-CHEAT: Mark puzzle as completed (app state + persistence)
      if (difficulty && markPuzzleCompleted) {
        markPuzzleCompleted(difficulty);
        persistPuzzleCompletion();
      }
    }
  }, [grid, solution, difficulty, markPuzzleCompleted, persistPuzzleCompletion]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isGivenCell = (row, col) => {
    if (!initialPuzzle) return false;
    return initialPuzzle[row][col] !== 0;
  };

  const isCorrectlyFilled = (row, col) => {
    if (!solution) return false;
    return grid[row][col] !== 0 && grid[row][col] === solution[row][col];
  };

  // ANTI-CHEAT: Enhanced conflict detection for proper mistake coloring
  const isIncorrect = (row, col) => {
    if (!solution) return false;
    if (grid[row][col] === 0) return false;
    if (isGivenCell(row, col)) return false;
    
    // Incorrect if doesn't match solution
    return grid[row][col] !== solution[row][col];
  };

  const hasConflict = (row, col, num) => {
    if (num === 0) return false;

    for (let c = 0; c < 9; c++) {
      if (c !== col && grid[row][c] === num) return true;
    }

    for (let r = 0; r < 9; r++) {
      if (r !== row && grid[r][col] === num) return true;
    }

    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if ((r !== row || c !== col) && grid[r][c] === num) return true;
      }
    }

    return false;
  };

  const handleCellClick = (row, col) => {
    setSelectedCell({ row, col });
    
    if (grid[row][col] !== 0) {
      setHighlightNumber(grid[row][col]);
      setSelectedNumber(grid[row][col]);
    } else {
      setHighlightNumber(null);
      setSelectedNumber(null); // Clear dial pad highlight when clicking empty cell
    }
  };

  const handleNumberInput = useCallback((num) => {
    if (!selectedCell) return;
    const { row, col } = selectedCell;
    if (isGivenCell(row, col)) return;
    if (isCorrectlyFilled(row, col)) return;

    // NOTES FIX: Use ref instead of state to get CURRENT value immediately
    // This prevents race condition when cell is selected before notes is toggled
    const notesMode = notesModeRef.current;

    setHistory(prev => [...prev, { grid: grid.map(r => [...r]), notes: notes.map(r => r.map(c => [...c])) }]);

    if (notesMode) {
      // NOTES MODE: Toggle note in cell
      setNotes(prev => {
        const newNotes = prev.map(r => r.map(c => [...c]));
        const cellNotes = newNotes[row][col];
        const index = cellNotes.indexOf(num);
        if (index > -1) {
          cellNotes.splice(index, 1);
        } else {
          cellNotes.push(num);
          cellNotes.sort();
        }
        return newNotes;
      });
    } else {
      // NORMAL MODE: Place number and clear all notes in that cell
      const newGrid = grid.map(r => [...r]);
      newGrid[row][col] = num;
      setGrid(newGrid);
      setHighlightNumber(num);
      setSelectedNumber(num);

      // REFINEMENT: Clear all notes in cell when final number placed
      setNotes(prev => {
        const newNotes = prev.map(r => r.map(c => [...c]));
        newNotes[row][col] = [];
        return newNotes;
      });

      // ANTI-CHEAT: Track mistakes in Standard mode
      if (mode === 'standard' && num !== 0 && solution) {
        if (hasConflict(row, col, num) || solution[row][col] !== num) {
          setMistakes(prev => prev + 1);
        }
      }
    }
  }, [selectedCell, grid, notes, mode, solution, isGivenCell, isCorrectlyFilled, hasConflict, notesModeRef]);

  const handleUndo = () => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    setGrid(lastState.grid);
    setNotes(lastState.notes);
    setHistory(prev => prev.slice(0, -1));
  };

  const handleClear = useCallback(() => {
    if (!selectedCell) return;
    const { row, col } = selectedCell;
    if (isGivenCell(row, col)) return;
    if (isCorrectlyFilled(row, col)) return;

    setHistory(prev => [...prev, { grid: grid.map(r => [...r]), notes: notes.map(r => r.map(c => [...c])) }]);
    
    const newGrid = grid.map(r => [...r]);
    newGrid[row][col] = 0;
    setGrid(newGrid);
    setHighlightNumber(null);
    setSelectedNumber(null);

    setNotes(prev => {
      const newNotes = prev.map(r => r.map(c => [...c]));
      newNotes[row][col] = [];
      return newNotes;
    });
  }, [selectedCell, grid, notes, isGivenCell, isCorrectlyFilled]);

  // ANTI-CHEAT: Exit behavior - Standard mode shows confirmation, Relaxed does not
  const handleExit = async () => {
    if (mode === 'standard') {
      // Standard mode: always show confirmation
      const confirmed = window.confirm(
        "Are you sure you want to exit?\n\nExiting will forfeit your attempt for today. You cannot return to this Standard solve."
      );
      if (confirmed) {
        // ANTI-CHEAT: Mark puzzle as attempted (prevents replay)
        await persistPuzzleAttempt();
        markAttemptUsed(difficulty, mode);
        goBack();
      }
    } else {
      // Relaxed mode: no confirmation, just exit
      goBack();
    }
  };

  // ANTI-CHEAT: Persist that puzzle was attempted (database + localStorage)
  const persistPuzzleAttempt = async () => {
    const today = new Date().toISOString().split('T')[0];
    const stateKey = `puzzle_state_${difficulty}_${today}`;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Logged-in: save to database (handled by markAttemptUsed + database trigger)
        // Note: solves table will have entry with partial data
      } else {
        // Guest: save to localStorage
        localStorage.setItem(stateKey, JSON.stringify({
          attempted: true,
          completed: false,
          mode: mode,
          date: today
        }));
      }
    } catch (error) {
      console.error('Error persisting attempt:', error);
    }
  };

  const handleSubmit = () => {
    setIsRunning(false);
    
    // Calculate actual elapsed time (for relaxed mode)
    const actualElapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
    const actualTimeFormatted = formatTime(actualElapsedSeconds);
    
    // Prepare complete solve data to pass to ResultScreen
    const solveData = {
      time: mode === 'standard' ? formatTime(time) : '00:00', // Standard shows timer, relaxed shows 00:00
      actualTime: actualTimeFormatted, // Actual elapsed time (used for relaxed mode display)
      difficulty: difficulty,
      mode: mode,
      mistakes: mistakes,
      isEligible: mode === 'standard' && mistakes < 3,
      timestamp: Date.now()
    };
    
    navigateTo('result', { 
      fromPuzzle: true, 
      difficulty,
      solveData 
    });
  };

  // Render Sudoku Grid
  const renderGrid = () => (
    <div style={{
      background: 'white',
      padding: isMobile ? '12px' : '16px',
      borderRadius: '12px',
      boxShadow: '0 8px 30px rgba(0,0,0,0.3)'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(9, 1fr)',
        gap: '0',
        aspectRatio: '1',
        border: '3px solid #1F2937'
      }}>
        {grid.map((row, rowIndex) => 
          row.map((cell, colIndex) => {
            const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
            const isHighlighted = highlightNumber && cell === highlightNumber && cell !== 0;
            const isSameRow = selectedCell?.row === rowIndex;
            const isSameCol = selectedCell?.col === colIndex;
            const isSameBox = selectedCell && 
              Math.floor(selectedCell.row / 3) === Math.floor(rowIndex / 3) &&
              Math.floor(selectedCell.col / 3) === Math.floor(colIndex / 3);
            const isGiven = isGivenCell(rowIndex, colIndex);
            const isWrong = isIncorrect(rowIndex, colIndex);

            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                onClick={() => handleCellClick(rowIndex, colIndex)}
                style={{
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // UI REFINEMENT: Increased by 10% (was 18px-24px, now 20px-26px for numbers, notes unchanged)
                  fontSize: cell !== 0 ? 'clamp(22px, 4.24vw, 29px)' : 'clamp(7px, 1.3vw, 9px)',
                  fontWeight: isGiven || isCorrectlyFilled(rowIndex, colIndex) ? '700' : '600',
                  // ANTI-CHEAT: Correct color logic
                  color: isGiven ? '#1F2937' :                    // Given cells: dark gray
                         isWrong ? '#DC6B19' :                    // Incorrect entries: dark orange
                         isCorrectlyFilled(rowIndex, colIndex) ? '#1e3a8a' : // Correct entries: dark blue
                         '#2563EB',                               // Empty or notes: medium blue
                  background: isSelected ? 'rgba(191, 219, 254, 0.6)' : 
                             isHighlighted ? 'rgba(191, 219, 254, 0.35)' :
                             isSameRow || isSameCol || isSameBox ? 'rgba(209, 213, 219, 0.4)' : 
                             'white',
                  borderRight: (colIndex + 1) % 3 === 0 && colIndex !== 8 ? '2px solid #1F2937' : '1px solid #D1D5DB',
                  borderBottom: (rowIndex + 1) % 3 === 0 && rowIndex !== 8 ? '2px solid #1F2937' : '1px solid #D1D5DB',
                  cursor: 'pointer',
                  transition: 'background 0.1s ease',
                  position: 'relative',
                  userSelect: 'none'
                }}
              >
                {cell !== 0 ? cell : (
                  notes[rowIndex][colIndex].length > 0 && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '1px',
                      width: '80%',
                      height: '80%',
                      fontSize: 'clamp(7px, 1.3vw, 10px)',
                      color: '#6B7280'
                    }}>
                      {[1,2,3,4,5,6,7,8,9].map(n => (
                        <div key={n} style={{ textAlign: 'center' }}>
                          {notes[rowIndex][colIndex].includes(n) ? n : ''}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // Loading state
  if (isLoadingPuzzle) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        fontSize: '18px',
        fontWeight: '600',
        gap: '16px',
        padding: '20px'
      }}>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div>Loading today's puzzle...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      padding: '16px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      {/* Warning banner if using fallback puzzle */}
      {puzzleError && (
        <div style={{
          background: '#FEF3C7',
          border: '2px solid #F59E0B',
          padding: '14px 18px',
          borderRadius: '12px',
          margin: '0 auto 20px auto',
          maxWidth: '900px',
          fontSize: '14px',
          color: '#92400E',
          fontWeight: '500',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          <div style={{ fontSize: '20px', marginBottom: '6px' }}>⚠️</div>
          <strong>Using practice puzzle</strong>
          <br />
          Today's official puzzle could not be loaded. Your solve will not be ranked.
        </div>
      )}
      
      <div style={{
        maxWidth: isMobile ? '550px' : '950px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{
          marginBottom: '12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700' }}>
              {difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : 'Hard'} Puzzle
            </h2>
            <div style={{ fontSize: '13px', opacity: 0.9, fontWeight: '500' }}>
              Mode: {mode === 'standard' ? 'Standard' : 'Relaxed — not ranked'}
            </div>
          </div>
          {isMobile && (
            <button
              onClick={handleExit}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
            >
              Exit
            </button>
          )}
        </div>

        {/* Main Content: Grid + Controls */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '20px' : '24px',
          alignItems: isMobile ? 'stretch' : 'flex-start'
        }}>
          {/* Left Side: Timer + Grid + Hint */}
          <div style={{ flex: isMobile ? '1' : '0 0 550px' }}>
            {/* Timer and Mistakes - Refined Status Bar */}
            <div style={{
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(10px)',
              padding: '14px 18px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}>
              {/* Show timer only in Standard mode */}
              {mode === 'standard' && (
                <div style={{ color: 'white' }}>
                  <div style={{ fontSize: '11px', opacity: 0.85, marginBottom: '3px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    Time
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: '700', fontFamily: 'monospace', letterSpacing: '1px' }}>
                    {formatTime(time)}
                  </div>
                </div>
              )}
              
              {mode === 'relaxed' && (
                <div style={{ color: 'white' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700' }}>
                    Relaxed Mode
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                    No timer, no pressure
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {/* Show mistakes only in Standard mode */}
                {mode === 'standard' && (
                  <div style={{ color: 'white', textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', opacity: 0.85, marginBottom: '3px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Mistakes
                    </div>
                    <div style={{
                      fontSize: '26px',
                      fontWeight: '700',
                      fontFamily: 'monospace',
                      letterSpacing: '1px',
                      color: mistakes >= 3 ? '#FCA5A5' : 'white'
                    }}>
                      {mistakes}/3
                    </div>
                  </div>
                )}
                {!isMobile && (
                  <button
                    onClick={handleExit}
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      color: 'white',
                      padding: '9px 18px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                    }}
                  >
                    Exit
                  </button>
                )}
              </div>
            </div>

            {/* Eligibility Warning - Only in Standard mode */}
            {mode === 'standard' && mistakes >= 3 && (
              <div style={{
                marginBottom: '12px',
                background: 'rgba(252, 165, 165, 0.2)',
                border: '1px solid rgba(252, 165, 165, 0.5)',
                borderRadius: '12px',
                padding: '11px 15px',
                color: '#FCA5A5',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                ⚠️ Leaderboard eligibility lost (3 mistakes). You can still complete the puzzle.
              </div>
            )}

            {renderGrid()}
            
            {/* Keyboard Hint - Desktop Only */}
            {!isMobile && (
              <div style={{
                marginTop: '10px',
                textAlign: 'center',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                💡 Keyboard: 1-9, arrows, N for notes, Backspace to clear
              </div>
            )}
          </div>

          {/* Right Side (Desktop) / Below (Mobile): Controls + Number Pad */}
          <div style={{ 
            flex: isMobile ? '1' : '0 0 300px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            justifyContent: isMobile ? 'flex-start' : 'center',
            paddingTop: isMobile ? '0' : '110px',
            marginLeft: isMobile ? '0' : '24px'
          }}>
            {/* Controls + Pad container (subtle “panel”) */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.10)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              borderRadius: '18px',
              padding: isMobile ? '12px' : '16px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.20)',
              backdropFilter: 'blur(10px)'
            }}>
              {/* Control Buttons — modern compact pills */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: isMobile ? '10px' : '12px',
                marginBottom: isMobile ? '12px' : '14px'
              }}>
                {/* Notes */}
                <button
                  onClick={() => setIsNotesMode(!isNotesMode)}
                  style={{
                    height: isMobile ? '42px' : '44px',
                    borderRadius: '14px',
                    border: isNotesMode ? '1px solid rgba(255,255,255,0.30)' : '1px solid rgba(255,255,255,0.18)',
                    background: isNotesMode
                      ? 'linear-gradient(135deg, rgba(59,130,246,0.95) 0%, rgba(37,99,235,0.95) 100%)'
                      : 'rgba(255, 255, 255, 0.92)',
                    color: isNotesMode ? '#FFFFFF' : '#1F2937',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: isNotesMode ? '0 8px 18px rgba(37,99,235,0.35)' : '0 6px 14px rgba(0,0,0,0.10)',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease'
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <span style={{ fontSize: '16px', lineHeight: 1 }}>✏️</span>
                  <span>{isNotesMode ? 'Notes ON' : 'Notes'}</span>
                </button>

                {/* Undo */}
                <button
                  onClick={handleUndo}
                  disabled={history.length === 0}
                  style={{
                    height: isMobile ? '42px' : '44px',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255, 255, 255, 0.92)',
                    color: '#1F2937',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: history.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: history.length === 0 ? 0.45 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.10)',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease'
                  }}
                  onMouseDown={(e) => {
                    if (history.length > 0) e.currentTarget.style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    if (history.length > 0) e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '16px', lineHeight: 1 }}>↩︎</span>
                  <span>Undo</span>
                </button>

                {/* Clear */}
                <button
                  onClick={handleClear}
                  style={{
                    height: isMobile ? '42px' : '44px',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255, 255, 255, 0.92)',
                    color: '#1F2937',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.10)',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease'
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <span style={{ fontSize: '16px', lineHeight: 1 }}>⌫</span>
                  <span>Clear</span>
                </button>
              </div>

              {/* Number Pad — linear keypad on mobile, 3x3 on desktop */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(9, 1fr)' : 'repeat(3, 1fr)',
                gap: isMobile ? '6px' : '12px'
              }}>
                {[1,2,3,4,5,6,7,8,9].map(num => {
                  const instanceCount = countNumberInstances(num);
                  const isCompleted = instanceCount === 9;
                  const isSelected = selectedNumber === num;

                  const baseBg = '#EEF2F7';
                  const selectedBg = '#E0ECFF';
                  const completedBg = '#E9EDF3';

                  return (
                    <button
                      key={num}
                      onClick={() => handleNumberInput(num)}
                      disabled={isCompleted}
                      style={{
                        height: isMobile ? '50px' : '78px',
                        borderRadius: '16px',
                        border: isSelected ? '2px solid rgba(59,130,246,0.70)' : '1px solid rgba(255,255,255,0.35)',
                        background: isCompleted ? completedBg : isSelected ? selectedBg : baseBg,
                        color: isCompleted ? 'rgba(30, 64, 175, 0.30)' : '#1E40AF',
                        fontSize: isMobile ? '22px' : '34px',
                        fontWeight: '700',
                        cursor: isCompleted ? 'not-allowed' : 'pointer',
                        boxShadow: isCompleted
                          ? 'inset 0 1px 0 rgba(255,255,255,0.55)'
                          : '0 10px 18px rgba(0,0,0,0.14)',
                        transition: 'transform 0.10s ease, box-shadow 0.10s ease, background 0.10s ease',
                        userSelect: 'none',
                        outline: 'none',
                        opacity: isCompleted ? 0.55 : 1,
                      }}
                      onMouseDown={(e) => {
                        if (!isCompleted) {
                          e.currentTarget.style.transform = 'translateY(1px) scale(0.99)';
                          e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.14)';
                        }
                      }}
                      onMouseUp={(e) => {
                        if (!isCompleted) {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 10px 18px rgba(0,0,0,0.14)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCompleted) {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = isCompleted
                            ? 'inset 0 1px 0 rgba(255,255,255,0.55)'
                            : '0 10px 18px rgba(0,0,0,0.14)';
                        }
                      }}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
        {/* Submit Button - Only shown when complete */}
        {puzzleComplete && (
          <div style={{ marginTop: '16px', maxWidth: isMobile ? '100%' : '550px' }}>
            <button
              onClick={handleSubmit}
              style={{
                width: '100%',
                padding: '15px',
                background: '#FF8C00',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(255, 140, 0, 0.4)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#E07B00';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 18px rgba(255, 140, 0, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C00';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(255, 140, 0, 0.4)';
              }}
            >
              Submit Puzzle ✓
            </button>
          </div>
        )}
      </div>
      <MinimalFooter />
    </div>
  );
}

export default SudokuPuzzle;